import { useState, useRef } from 'react';
import { useLanguage } from '../utils/i18n';
import { useNotification } from '../utils/NotificationContext';
import { useAuth } from '../utils/AuthContext';
import { supabase } from '../utils/supabase';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  Printer,
  Download,
  Palette,
  Type,
  Eye,
  Settings2,
  Save,
  RotateCcw,
  Sparkles,
  X,
  FileText
} from 'lucide-react';

const PRESET_COLORS = [
  { bg: '#ffffff', text: '#0f172a', label: 'Clean White' },
  { bg: '#0b111c', text: '#f8fafc', label: 'Dark Navy' },
  { bg: '#171c26', text: '#f8fafc', label: 'Slate' },
  { bg: '#1a73e8', text: '#ffffff', label: 'Royal Blue' },
  { bg: '#7c3aed', text: '#ffffff', label: 'Purple' },
  { bg: '#10b981', text: '#ffffff', label: 'Emerald' },
  { bg: '#ef4444', text: '#ffffff', label: 'Red' },
  { bg: '#f97316', text: '#ffffff', label: 'Orange' },
  { bg: '#f59e0b', text: '#1a1a2e', label: 'Amber' },
  { bg: '#ec4899', text: '#ffffff', label: 'Pink' },
  { bg: '#06b6d4', text: '#ffffff', label: 'Cyber' },
];

export default function CustomPosterStudio({ business, onClose, qrDataUrl, registrationUrl, isDemo }) {
  const { t } = useLanguage();
  const { showNotification } = useNotification();
  const { refetchBusiness } = useAuth();

  const savedSettings = business?.poster_settings || {};

  const [settings, setSettings] = useState({
    bgColor: savedSettings.bgColor || '#ffffff',
    textColor: savedSettings.textColor || '#0f172a',
    accentColor: savedSettings.accentColor || business?.color || '#1a73e8',
    headline: savedSettings.headline !== undefined ? savedSettings.headline : (business?.name || 'My Business'),
    subtitle: savedSettings.subtitle !== undefined ? savedSettings.subtitle : (t('print_poster_subtitle') || 'Scan to join our digital loyalty program and earn instant rewards.'),
    rewardHighlight: savedSettings.rewardHighlight || ('🎁 ' + (t('default_reward_highlight') || 'Collect 10 stamps for your free reward!')),
    showReward: savedSettings.showReward !== undefined ? savedSettings.showReward : true,
    showLogo: savedSettings.showLogo !== undefined ? savedSettings.showLogo : true,
    showSteps: savedSettings.showSteps !== undefined ? savedSettings.showSteps : true,
    showPoweredBy: savedSettings.showPoweredBy !== undefined ? savedSettings.showPoweredBy : true,
    format: savedSettings.format || 'a4',
    step1Text: savedSettings.step1Text || t('print_poster_step_1') || '1. Scan QR',
    step2Text: savedSettings.step2Text || t('print_poster_step_2') || '2. Register in 3s',
    step3Text: savedSettings.step3Text || t('print_poster_step_3') || '3. Add to Wallet'
  });

  const [activeTab, setActiveTab] = useState('colors'); // 'colors' | 'content' | 'layout'
  const [mobileTab, setMobileTab] = useState('preview'); // 'preview' | 'controls' on mobile
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const printAreaRef = useRef(null);

  const updateSetting = (key, val) => {
    setSettings(prev => ({ ...prev, [key]: val }));
  };

  const applyColorPreset = (preset) => {
    setSettings(prev => ({
      ...prev,
      bgColor: preset.bg,
      textColor: preset.text
    }));
  };

  const handleReset = () => {
    setSettings({
      bgColor: '#ffffff',
      textColor: '#0f172a',
      accentColor: business?.color || '#1a73e8',
      headline: business?.name || 'My Business',
      subtitle: t('print_poster_subtitle') || 'Scan to join our digital loyalty program and earn instant rewards.',
      rewardHighlight: '🎁 ' + (t('default_reward_highlight') || 'Collect 10 stamps for your free reward!'),
      showReward: true,
      showLogo: true,
      showSteps: true,
      showPoweredBy: true,
      format: 'a4',
      step1Text: t('print_poster_step_1') || '1. Scan QR',
      step2Text: t('print_poster_step_2') || '2. Register in 3s',
      step3Text: t('print_poster_step_3') || '3. Add to Wallet'
    });
    showNotification('Reset to default print design', 'info');
  };

  const handleSaveSettings = async () => {
    if (isDemo) {
      showNotification('Settings saved in demo mode!', 'success');
      return;
    }
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({ poster_settings: settings })
        .eq('id', business.id);

      if (error) throw error;
      showNotification('Custom print design saved!', 'success');
      if (refetchBusiness) refetchBusiness(business.user_id);
    } catch (err) {
      console.error('Failed to save poster settings:', err);
      showNotification('Saved locally (DB column pending migration)', 'info');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!printAreaRef.current) return;
    setIsGeneratingPdf(true);
    try {
      const canvas = await html2canvas(printAreaRef.current, {
        scale: 3, // 300 DPI high resolution
        useCORS: true,
        logging: false,
        backgroundColor: settings.bgColor
      });

      const imgData = canvas.toDataURL('image/png');
      const isA4 = settings.format === 'a4';

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: isA4 ? 'a4' : 'a5'
      });

      const pageWidth = isA4 ? 210 : 148;
      const pageHeight = isA4 ? 297 : 210;

      pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
      const fileName = `${(settings.headline || business?.name || 'loyalty').toLowerCase().replace(/\s+/g, '-')}-counter-stand.pdf`;
      pdf.save(fileName);
      showNotification('PDF downloaded successfully! Ready to print.', 'success');
    } catch (err) {
      console.error('Error generating PDF:', err);
      showNotification('Failed to generate PDF document.', 'error');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Contrast helper for borders and secondary elements
  const isDarkBg = settings.bgColor.toLowerCase() === '#0b111c' ||
    settings.bgColor.toLowerCase() === '#171c26' ||
    settings.bgColor.toLowerCase() === '#0f172a' ||
    settings.bgColor.toLowerCase() === '#000000';

  const cardBorderColor = isDarkBg ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)';
  const secondaryTextColor = isDarkBg ? 'rgba(255, 255, 255, 0.65)' : 'rgba(15, 23, 42, 0.65)';

  return (
    <div className="modal-overlay" style={{ zIndex: 1100, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        className="modal glass-card"
        style={{
          width: '100%',
          maxWidth: 1040,
          maxHeight: '92vh',
          background: 'var(--surface)',
          borderRadius: 24,
          border: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
          position: 'relative'
        }}
      >
        {/* Modal Header */}
        <div
          className="modal-header"
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--surface)',
            flexShrink: 0
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Sparkles size={20} color="var(--primary)" />
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: 'var(--text)' }}>
                {t('customize_counter_stand') || 'Customize Counter Stand & Poster'}
              </h2>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                {t('counter_stand_desc') || 'Pick your brand colors and download high-resolution print PDF'}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={handleReset}
              className="btn btn-outline"
              style={{ fontSize: 12, padding: '6px 12px', height: 32 }}
              title="Reset"
            >
              <RotateCcw size={14} style={{ marginRight: 6 }} />
              <span className="hide-mobile">Reset</span>
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={isSaving}
              className="btn btn-primary"
              style={{ fontSize: 12, padding: '6px 14px', height: 32 }}
            >
              <Save size={14} style={{ marginRight: 6 }} />
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button className="modal-close" onClick={onClose} style={{ marginLeft: 6 }}>
              ✕
            </button>
          </div>
        </div>

        {/* Mobile View Toggle */}
        <div
          className="mobile-tab-bar"
          style={{
            display: 'none',
            padding: '8px 16px',
            background: 'var(--bg)',
            borderBottom: '1px solid var(--border)',
            gap: 8
          }}
        >
          <button
            onClick={() => setMobileTab('preview')}
            className={`btn btn-sm ${mobileTab === 'preview' ? 'btn-primary' : 'btn-outline'}`}
            style={{ flex: 1, justifyContent: 'center' }}
          >
            <Eye size={14} style={{ marginRight: 6 }} /> Full Preview
          </button>
          <button
            onClick={() => setMobileTab('controls')}
            className={`btn btn-sm ${mobileTab === 'controls' ? 'btn-primary' : 'btn-outline'}`}
            style={{ flex: 1, justifyContent: 'center' }}
          >
            <Palette size={14} style={{ marginRight: 6 }} /> Colors & Content
          </button>
        </div>

        {/* Studio Workspace */}
        <div
          style={{
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            flexDirection: 'row'
          }}
          className="customizer-workspace"
        >
          {/* LEFT COLUMN: Clean Full-Page Stand Preview */}
          <div
            style={{
              flex: '0 0 460px',
              maxWidth: 460,
              background: 'var(--bg)',
              padding: '20px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRight: '1px solid var(--border)',
              position: 'relative',
              overflow: 'hidden'
            }}
            className={`stand-preview-pane ${mobileTab === 'controls' ? 'hide-mobile' : ''}`}
          >
            {/* The Entire Stand Sheet Preview (Fit 100% top-to-bottom without scroll) */}
            <div
              style={{
                width: 350,
                height: 495, // 1 : 1.414 ratio (A4/A5)
                boxShadow: '0 20px 45px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                borderRadius: 20,
                overflow: 'hidden',
                position: 'relative',
                flexShrink: 0
              }}
            >
              {/* THE EXACT CAPTURED PRINT ELEMENT */}
              <div
                ref={printAreaRef}
                id="print-poster-container"
                style={{
                  width: '100%',
                  height: '100%',
                  background: settings.bgColor,
                  color: settings.textColor,
                  fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
                  padding: '24px 22px 18px 22px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textAlign: 'center',
                  position: 'relative'
                }}
              >
                {/* Header: Logo & Title */}
                <div style={{ width: '100%' }}>
                  {settings.showLogo && business?.logo_url ? (
                    <div style={{ marginBottom: 8, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img
                        src={business.logo_url}
                        alt="Logo"
                        style={{
                          maxHeight: 38,
                          maxWidth: 160,
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  ) : null}

                  <h1
                    style={{
                      fontSize: 24,
                      fontWeight: 800,
                      margin: '0 0 4px 0',
                      color: settings.textColor,
                      lineHeight: 1.15,
                      letterSpacing: '-0.5px'
                    }}
                  >
                    {settings.headline || business?.name || 'My Business'}
                  </h1>

                  <p
                    style={{
                      fontSize: 11,
                      color: secondaryTextColor,
                      maxWidth: 290,
                      margin: '0 auto',
                      lineHeight: 1.35,
                      fontWeight: 500
                    }}
                  >
                    {settings.subtitle}
                  </p>

                  {/* Incentive Banner */}
                  {settings.showReward && settings.rewardHighlight && (
                    <div
                      style={{
                        marginTop: 8,
                        display: 'inline-block',
                        background: `${settings.accentColor}18`,
                        color: settings.accentColor,
                        border: `1px solid ${settings.accentColor}40`,
                        borderRadius: 99,
                        padding: '4px 12px',
                        fontSize: 10,
                        fontWeight: 700
                      }}
                    >
                      {settings.rewardHighlight}
                    </div>
                  )}
                </div>

                {/* QR Code */}
                <div style={{ margin: '10px 0' }}>
                  <div
                    style={{
                      background: '#ffffff',
                      padding: 14,
                      borderRadius: 18,
                      display: 'inline-block',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                      border: `2px solid ${settings.accentColor}40`
                    }}
                  >
                    {qrDataUrl ? (
                      <img
                        src={qrDataUrl}
                        alt="Registration QR"
                        style={{ width: 140, height: 140, display: 'block', borderRadius: 6 }}
                      />
                    ) : (
                      <QRCodeSVG value={registrationUrl} size={140} level="H" includeMargin={false} />
                    )}
                  </div>
                </div>

                {/* 3 Step Instructions */}
                {settings.showSteps && (
                  <div
                    style={{
                      width: '100%',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 6,
                      marginBottom: 8
                    }}
                  >
                    <div
                      style={{
                        background: isDarkBg ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                        border: `1px solid ${cardBorderColor}`,
                        borderRadius: 10,
                        padding: '6px 4px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <span style={{ fontSize: 13 }}>📸</span>
                      <span style={{ fontSize: 9.5, fontWeight: 700, color: settings.textColor }}>
                        {settings.step1Text}
                      </span>
                    </div>

                    <div
                      style={{
                        background: isDarkBg ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                        border: `1px solid ${cardBorderColor}`,
                        borderRadius: 10,
                        padding: '6px 4px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <span style={{ fontSize: 13 }}>📝</span>
                      <span style={{ fontSize: 9.5, fontWeight: 700, color: settings.textColor }}>
                        {settings.step2Text}
                      </span>
                    </div>

                    <div
                      style={{
                        background: isDarkBg ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                        border: `1px solid ${cardBorderColor}`,
                        borderRadius: 10,
                        padding: '6px 4px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <span style={{ fontSize: 13 }}>📲</span>
                      <span style={{ fontSize: 9.5, fontWeight: 700, color: settings.textColor }}>
                        {settings.step3Text}
                      </span>
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div
                  style={{
                    width: '100%',
                    borderTop: `1px solid ${cardBorderColor}`,
                    paddingTop: 8
                  }}
                >
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 10.5, color: settings.textColor }}>
                    {t('print_poster_no_app') || 'No app required. Just scan & save to Wallet.'}
                  </p>
                  {settings.showPoweredBy && (
                    <div style={{ fontSize: 9, color: secondaryTextColor, marginTop: 2, opacity: 0.8 }}>
                      {t('print_poster_powered_by') || 'Powered by Wafiyo Loyalty'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Export Action Bar */}
            <div style={{ marginTop: 14, display: 'flex', gap: 8, width: 350 }}>
              <button
                onClick={handleDownloadPdf}
                disabled={isGeneratingPdf}
                className="btn btn-primary"
                style={{ flex: 1, justifyContent: 'center' }}
              >
                <Download size={15} style={{ marginRight: 6 }} />
                {isGeneratingPdf ? 'Generating PDF...' : 'Download PDF'}
              </button>
              <button
                onClick={handlePrint}
                className="btn btn-outline"
                style={{ flexShrink: 0 }}
                title="Print directly"
              >
                <Printer size={15} />
              </button>
            </div>
          </div>

          {/* RIGHT COLUMN: App-Themed Design Controls */}
          <div
            style={{
              flex: 1,
              padding: '20px 24px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 20
            }}
            className={`controls-pane ${mobileTab === 'preview' ? 'hide-mobile' : ''}`}
          >
            {/* Top Tabs */}
            <div style={{ display: 'flex', background: 'var(--bg)', borderRadius: 12, padding: 4, border: '1px solid var(--border)' }}>
              <button
                onClick={() => setActiveTab('colors')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 10,
                  border: 'none',
                  background: activeTab === 'colors' ? 'var(--surface)' : 'transparent',
                  color: activeTab === 'colors' ? 'var(--text)' : 'var(--text-muted)',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6
                }}
              >
                <Palette size={15} /> {t('colors') || 'Colors'}
              </button>
              <button
                onClick={() => setActiveTab('content')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 10,
                  border: 'none',
                  background: activeTab === 'content' ? 'var(--surface)' : 'transparent',
                  color: activeTab === 'content' ? 'var(--text)' : 'var(--text-muted)',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6
                }}
              >
                <Type size={15} /> {t('content') || 'Content'}
              </button>
              <button
                onClick={() => setActiveTab('layout')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 10,
                  border: 'none',
                  background: activeTab === 'layout' ? 'var(--surface)' : 'transparent',
                  color: activeTab === 'layout' ? 'var(--text)' : 'var(--text-muted)',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6
                }}
              >
                <Settings2 size={15} /> {t('format') || 'Format'}
              </button>
            </div>

            {/* TAB 1: COLORS (User chooses background, text, and accent color) */}
            {activeTab === 'colors' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                {/* Color Swatches */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>{t('color_preset') || 'Color Palette Presets'}</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {PRESET_COLORS.map((preset, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => applyColorPreset(preset)}
                        title={preset.label}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          border: settings.bgColor === preset.bg ? '2px solid var(--primary)' : '1px solid var(--border)',
                          background: preset.bg,
                          cursor: 'pointer',
                          boxShadow: settings.bgColor === preset.bg ? '0 0 8px var(--primary)' : 'none'
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Direct Color Pickers using app's colors-grid */}
                <div className="colors-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>{t('background_color') || 'Background'}</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="color"
                        value={settings.bgColor}
                        onChange={e => updateSetting('bgColor', e.target.value)}
                        style={{ width: 44, padding: 2, height: 38, borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer' }}
                      />
                      <input
                        className="form-control"
                        value={settings.bgColor}
                        onChange={e => updateSetting('bgColor', e.target.value)}
                        style={{ flex: 1, fontSize: 13, fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>{t('text_color') || 'Text Color'}</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="color"
                        value={settings.textColor}
                        onChange={e => updateSetting('textColor', e.target.value)}
                        style={{ width: 44, padding: 2, height: 38, borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer' }}
                      />
                      <input
                        className="form-control"
                        value={settings.textColor}
                        onChange={e => updateSetting('textColor', e.target.value)}
                        style={{ flex: 1, fontSize: 13, fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>{t('accent_color') || 'Accent / QR'}</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="color"
                        value={settings.accentColor}
                        onChange={e => updateSetting('accentColor', e.target.value)}
                        style={{ width: 44, padding: 2, height: 38, borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer' }}
                      />
                      <input
                        className="form-control"
                        value={settings.accentColor}
                        onChange={e => updateSetting('accentColor', e.target.value)}
                        style={{ flex: 1, fontSize: 13, fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Logo & Branding Options */}
                <div style={{ background: 'var(--bg)', padding: 14, borderRadius: 14, border: '1px solid var(--border)' }}>
                  <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 10 }}>
                    {t('branding') || 'Branding Options'}
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={settings.showLogo}
                        onChange={e => updateSetting('showLogo', e.target.checked)}
                      />
                      <span>{t('show_logo_on_poster') || 'Display business logo at top'}</span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={settings.showPoweredBy}
                        onChange={e => updateSetting('showPoweredBy', e.target.checked)}
                      />
                      <span>{t('show_powered_by') || 'Display "Powered by Wafiyo" in footer'}</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: CONTENT & TEXT */}
            {activeTab === 'content' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>{t('headline') || 'Poster Headline'}</label>
                  <input
                    className="form-control"
                    value={settings.headline}
                    onChange={e => updateSetting('headline', e.target.value)}
                    placeholder={business.name}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>{t('subtitle') || 'Subtitle / Description'}</label>
                  <textarea
                    rows={2}
                    className="form-control"
                    value={settings.subtitle}
                    onChange={e => updateSetting('subtitle', e.target.value)}
                    placeholder="Scan to join our loyalty programme and earn rewards"
                  />
                </div>

                {/* Reward Callout */}
                <div style={{ background: 'var(--bg)', padding: 14, borderRadius: 14, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ fontSize: 13, fontWeight: 700, margin: 0 }}>
                      {t('reward_badge') || 'Reward / Incentive Highlight'}
                    </label>
                    <input
                      type="checkbox"
                      checked={settings.showReward}
                      onChange={e => updateSetting('showReward', e.target.checked)}
                    />
                  </div>
                  {settings.showReward && (
                    <input
                      className="form-control"
                      value={settings.rewardHighlight}
                      onChange={e => updateSetting('rewardHighlight', e.target.value)}
                      placeholder="e.g. 🎁 10th visit free reward!"
                    />
                  )}
                </div>

                {/* 3 Step Guides */}
                <div style={{ background: 'var(--bg)', padding: 14, borderRadius: 14, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <label style={{ fontSize: 13, fontWeight: 700, margin: 0 }}>
                      {t('instructions_box') || '3-Step Customer Guide'}
                    </label>
                    <input
                      type="checkbox"
                      checked={settings.showSteps}
                      onChange={e => updateSetting('showSteps', e.target.checked)}
                    />
                  </div>

                  {settings.showSteps && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <input
                        className="form-control"
                        value={settings.step1Text}
                        onChange={e => updateSetting('step1Text', e.target.value)}
                      />
                      <input
                        className="form-control"
                        value={settings.step2Text}
                        onChange={e => updateSetting('step2Text', e.target.value)}
                      />
                      <input
                        className="form-control"
                        value={settings.step3Text}
                        onChange={e => updateSetting('step3Text', e.target.value)}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TAB 3: LAYOUT & SIZING */}
            {activeTab === 'layout' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>{t('paper_size') || 'Paper Sizing'}</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => updateSetting('format', 'a4')}
                      className={`btn ${settings.format === 'a4' ? 'btn-primary' : 'btn-outline'}`}
                      style={{ padding: '14px 12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', height: 'auto', textAlign: 'left' }}
                    >
                      <strong style={{ fontSize: 14 }}>A4 Flyer / Poster</strong>
                      <span style={{ fontSize: 11, opacity: 0.8 }}>210 × 297 mm · Standard sheet</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => updateSetting('format', 'a5')}
                      className={`btn ${settings.format === 'a5' ? 'btn-primary' : 'btn-outline'}`}
                      style={{ padding: '14px 12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', height: 'auto', textAlign: 'left' }}
                    >
                      <strong style={{ fontSize: 14 }}>A5 Counter Stand</strong>
                      <span style={{ fontSize: 11, opacity: 0.8 }}>148 × 210 mm · Acrylic desk stand</span>
                    </button>
                  </div>
                </div>

                <div style={{ background: 'var(--bg)', padding: 14, borderRadius: 14, border: '1px solid var(--border)' }}>
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    💡 <strong>{t('print_tip') || 'Print Tip:'}</strong> For the best quality on regular office printers, choose a white background with your brand accent color. Insert the printout into an acrylic stand at your checkout point.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Global style overrides */}
        <style>{`
          @media (max-width: 768px) {
            .mobile-tab-bar {
              display: flex !important;
            }
            .customizer-workspace {
              flex-direction: column !important;
            }
            .stand-preview-pane {
              flex: 1 1 100% !important;
              max-width: 100% !important;
              border-right: none !important;
              padding: 16px !important;
            }
            .hide-mobile {
              display: none !important;
            }
          }
          @media print {
            body * {
              visibility: hidden;
            }
            #print-poster-container, #print-poster-container * {
              visibility: visible;
            }
            #print-poster-container {
              position: fixed;
              left: 0;
              top: 0;
              width: 100vw;
              height: 100vh;
              margin: 0;
              padding: 40px;
              box-shadow: none;
              border-radius: 0;
            }
          }
        `}</style>
      </div>
    </div>
  );
}
